import { Info } from 'lucide-react'

import { HelpCategory } from './helpContent'
import TutorialCard from './TutorialCard'
import { HelpTutorial, getTutorialsForSection, getTutorialsForTopic } from './tutorials'

const helpCategoryIcons: Record<string, string> = {
  welcome: '',
  orders: '',
  products: '',
  media: '',
  settings: '',
}

type HelpCategoryContentProps = {
  category: HelpCategory
  withAnchors?: boolean
  stickyHeader?: boolean
  sectionIds?: string[]
  showCategoryHeader?: boolean
  showSectionHeader?: boolean
  onPlayTutorial?: (tutorial: HelpTutorial) => void
}

const HelpCategoryContent = ({
  category,
  withAnchors = false,
  stickyHeader = false,
  sectionIds,
  showCategoryHeader = true,
  showSectionHeader = true,
  onPlayTutorial,
}: HelpCategoryContentProps) => {
  const visibleSections = sectionIds
    ? category.sections.filter((section) => sectionIds.includes(section.id))
    : category.sections

  return (
    <section
      id={withAnchors ? `help-category-${category.id}` : undefined}
      className={`${withAnchors ? 'scroll-mt-[24px]' : ''} flex flex-col gap-xl`}
    >
      {showCategoryHeader && (
        <div
        className={`${
          stickyHeader ? 'sticky top-[24px] z-[10]' : ''
        } flex items-center gap-lg rounded-md bg-gray-100 py-lg pr-lg`}
        >
          <span
            aria-hidden="true"
            className="font-icon-regular flex h-[88px] w-[88px] shrink-0 items-center justify-center border-r border-gray-300 bg-transparent text-[42px] font-normal text-gray-900"
          >
            {helpCategoryIcons[category.id]}
          </span>
          <div className="flex min-w-0 flex-col gap-xs">
            <div className="text-title-xl text-gray-900">{category.title}</div>
            <div className="max-w-[900px] text-text-md text-gray-400">{category.description}</div>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-xxl">
        {visibleSections.map((section) => {
          const sectionTutorials = getTutorialsForSection(section.id)

          return (
            <section
              key={section.id}
              id={withAnchors ? `help-section-${section.id}` : undefined}
              className={`${withAnchors ? 'scroll-mt-[176px]' : ''} flex max-w-[1000px] flex-col gap-md`}
            >
              {showSectionHeader ? (
                <div className="mb-md flex flex-col gap-xs border-b border-gray-200 pb-md">
                  <div className="text-title-lg text-gray-900">{section.title}</div>
                  <div className="text-text-md text-gray-400">{section.description}</div>
                </div>
              ) : (
                <div className="mb-md border-b border-gray-200 pb-md text-text-md text-gray-400">
                  {section.description}
                </div>
              )}

              {onPlayTutorial && sectionTutorials.length > 0 && (
                <div className="flex flex-col gap-sm">
                  {sectionTutorials.map((tutorial) => (
                    <TutorialCard
                      key={tutorial.id}
                      tutorial={tutorial}
                      onPlay={onPlayTutorial}
                      compact
                    />
                  ))}
                </div>
              )}

              <div className="flex flex-col divide-y divide-gray-200">
                {section.topics.map((topic) => {
                  const topicTutorials = getTutorialsForTopic(topic.id)

                  return (
                    <article key={topic.id} className="flex flex-col gap-md py-lg first:pt-0 last:pb-0">
                      <div className="flex flex-col gap-xs">
                        <h3 className="text-title-md text-gray-900">{topic.title}</h3>
                        <p className="text-text-md text-gray-500">{topic.summary}</p>
                      </div>

                      {topic.steps && topic.steps.length > 0 && (
                        <ol className="list-inside list-decimal flex flex-col gap-xs text-text-md text-gray-700">
                          {topic.steps.map((step) => (
                            <li key={step}>{step}</li>
                          ))}
                        </ol>
                      )}

                      {topic.notes && topic.notes.length > 0 && (
                        <div className="flex items-center gap-sm py-md text-text-sm text-gray-500">
                          <Info aria-hidden="true" className="h-[18px] w-[18px] shrink-0" />
                          <div className="flex flex-col gap-xs">
                            {topic.notes.map((note) => (
                              <div key={note}>{note}</div>
                            ))}
                          </div>
                        </div>
                      )}

                      {onPlayTutorial && topicTutorials.length > 0 && (
                        <div className="flex flex-col gap-sm">
                          {topicTutorials.map((tutorial) => (
                            <TutorialCard
                              key={tutorial.id}
                              tutorial={tutorial}
                              onPlay={onPlayTutorial}
                              compact
                            />
                          ))}
                        </div>
                      )}

                      {topic.media && topic.media.length > 0 && (
                        <div className="flex flex-col gap-sm">
                          {topic.media.map((media) => (
                            <figure key={media.src} className="flex flex-col gap-xs">
                              {media.type === 'image' ? (
                                <img
                                  src={media.src}
                                  alt={media.title || topic.title}
                                  className="w-full rounded-md border border-gray-200 bg-gray-50 object-cover"
                                />
                              ) : (
                                <video
                                  src={media.src}
                                  className="w-full rounded-md border border-gray-200 bg-gray-50"
                                  controls
                                />
                              )}
                              {(media.title || media.caption) && (
                                <figcaption className="text-text-sm text-gray-400">
                                  {media.title && <span className="font-medium text-gray-500">{media.title}</span>}
                                  {media.title && media.caption && <span> - </span>}
                                  {media.caption}
                                </figcaption>
                              )}
                            </figure>
                          ))}
                        </div>
                      )}
                    </article>
                  )
                })}
              </div>
            </section>
          )
        })}
      </div>
    </section>
  )
}

export default HelpCategoryContent
